/**
 * Vue "Timeline pleine largeur" de la regulation (demande utilisateur) :
 * frise continue sur plusieurs jours consecutifs (pas de coupure a 22h, pas
 * besoin de changer de jour pour voir la nuit/le lendemain), affichee sur une
 * page dediee pour profiter de toute la largeur disponible plutot que de
 * partager l'espace avec les panneaux du tableau de bord Regulation.
 *
 * Contrairement a la timeline du tableau de bord (RegulationPage.tsx), le
 * glisser-deposer n'est pas supporte ici (reporterait une refonte complete de
 * la logique de drag, couplee aux heures "HH:MM" d'une seule journee) - la
 * modification (equipe/heure) se fait en cliquant sur un bloc, via la meme
 * modale `EditAffectationModal` que le tableau de bord.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { regulationService, type TimelineEquipe, type TimelineEquipeAffectation } from '@/services/regulationService';
import { useRegulationMercure, type RegulationEvent } from '@/hooks/useMercure';
import { NowLine } from '@/components/regulation/NowLine';
import { EditAffectationModal } from '@/components/regulation/EditAffectationModal';
import { STATUT_TIMELINE_COLORS } from '@/components/regulation/statutColors';

const HEURE_WIDTH = 60; // px, coherent avec RegulationPage.tsx
const HEURES_PAR_JOUR = 24;
const QUERY_KEY = 'regulation-timeline-range';

const JOURS_OPTIONS = [2, 3, 5, 7];

export function RegulationTimelinePage() {
  const queryClient = useQueryClient();
  const [dateDebut, setDateDebut] = useState(() => new Date().toISOString().split('T')[0]);
  const [nbJours, setNbJours] = useState(3);
  const [editAffectation, setEditAffectation] = useState<{
    interventionId: number;
    numeroOrdre: string;
    parking: string;
    zone: number;
    statut: string;
    currentEquipeId: number;
    heurePlanifiee: string;
    heureSouhaitee: string;
  } | null>(null);

  const { data: timeline, isLoading } = useQuery({
    queryKey: [QUERY_KEY, dateDebut, nbJours],
    queryFn: () => regulationService.getTimelineRange(dateDebut, nbJours),
    refetchInterval: 60000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });

  const reassignMutation = useMutation({
    mutationFn: regulationService.reassign,
    onSuccess: invalidate,
  });

  const updateScheduleMutation = useMutation({
    mutationFn: regulationService.updateSchedule,
    onSuccess: invalidate,
  });

  // Rafraichit la frise sur les memes evenements que le tableau de bord
  // (cf RegulationPage.tsx) - en particulier les evenements jalon_* qui ne
  // declenchaient aucun rafraichissement avant le Lot 5 (#125).
  useRegulationMercure((event: RegulationEvent) => {
    if (event.type !== 'retard_detected' && event.type !== 'conflit_detected') {
      invalidate();
    }
  });

  const totalHeures = nbJours * HEURES_PAR_JOUR;
  const totalWidth = totalHeures * HEURE_WIDTH;
  const windowStart = useMemo(() => new Date(`${dateDebut}T00:00:00`), [dateDebut]);
  const totalMinutes = totalHeures * 60;

  const getPositionPercent = (iso: string | null): number | null => {
    if (!iso) return null;
    const minutes = (new Date(iso).getTime() - windowStart.getTime()) / 60000;
    if (minutes < 0 || minutes > totalMinutes) return null;
    return (minutes / totalMinutes) * 100;
  };

  const shiftWindow = (deltaJours: number) => {
    const d = new Date(`${dateDebut}T00:00:00`);
    d.setDate(d.getDate() + deltaJours);
    setDateDebut(d.toISOString().split('T')[0]);
  };

  const goToToday = () => setDateDebut(new Date().toISOString().split('T')[0]);

  return (
    <div className="-m-6 h-[calc(100vh-4rem)] flex flex-col bg-gray-100">
      {/* En-tete */}
      <div className="bg-white border-b px-4 py-3 flex items-center gap-4 flex-wrap flex-shrink-0">
        <Link to="/regulation" className="flex items-center gap-1 text-gray-600 hover:text-gray-900 text-sm">
          <ArrowLeftIcon className="w-4 h-4" />
          Tableau de bord
        </Link>
        <h1 className="text-lg font-semibold text-gray-800">Timeline continue</h1>

        <div className="flex items-center gap-2 ml-auto">
          <button onClick={() => shiftWindow(-nbJours)} className="btn-secondary text-sm px-2 py-1">
            ← {nbJours}j
          </button>
          <input
            type="date"
            value={dateDebut}
            onChange={(e) => setDateDebut(e.target.value)}
            className="input text-sm py-1"
          />
          <button onClick={() => shiftWindow(nbJours)} className="btn-secondary text-sm px-2 py-1">
            {nbJours}j →
          </button>
          <button onClick={goToToday} className="btn-secondary text-sm px-2 py-1">
            Aujourd'hui
          </button>

          <select
            value={nbJours}
            onChange={(e) => setNbJours(Number(e.target.value))}
            className="input text-sm py-1"
          >
            {JOURS_OPTIONS.map((n) => (
              <option key={n} value={n}>{n} jours</option>
            ))}
          </select>
        </div>
      </div>

      {/* Frise */}
      <div className="flex-1 overflow-auto relative">
        {isLoading || !timeline ? (
          <div className="p-6 text-gray-500">Chargement...</div>
        ) : (
          <div style={{ width: `${160 + totalWidth}px` }}>
            {/* En-tete jours */}
            <div className="flex border-b bg-gray-50 sticky top-0 z-10">
              <div className="w-40 flex-shrink-0 px-4 py-2 font-medium text-gray-700 border-r bg-gray-50 sticky left-0 z-10">
                Equipe
              </div>
              {timeline.jours.map((jour) => (
                <div
                  key={jour}
                  className="text-center text-sm font-semibold text-gray-700 py-2 border-r-2 border-gray-300"
                  style={{ width: `${HEURES_PAR_JOUR * HEURE_WIDTH}px` }}
                >
                  {new Date(`${jour}T00:00:00`).toLocaleDateString('fr-FR', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                  })}
                </div>
              ))}
            </div>

            {/* En-tete heures */}
            <div className="flex border-b bg-gray-50 sticky z-10" style={{ top: '37px' }}>
              <div className="w-40 flex-shrink-0 border-r bg-gray-50 sticky left-0 z-10" />
              {timeline.jours.map((jour) =>
                Array.from({ length: HEURES_PAR_JOUR }, (_, h) => (
                  <div
                    key={`${jour}-${h}`}
                    className="flex-shrink-0 text-center text-xs text-gray-500 py-1 border-r border-gray-200"
                    style={{ width: `${HEURE_WIDTH}px` }}
                  >
                    {String(h).padStart(2, '0')}:00
                  </div>
                ))
              )}
            </div>

            {/* Ligne "heure actuelle" */}
            <div className="absolute pointer-events-none" style={{ left: '160px', width: `${totalWidth}px`, top: '69px', bottom: 0 }}>
              <NowLine getPositionPercent={() => getPositionPercent(new Date().toISOString())} />
            </div>

            {/* Lignes par equipe */}
            {timeline.equipes.map((equipe) => (
              <EquipeTimelineRow
                key={equipe.id}
                equipe={equipe}
                totalWidth={totalWidth}
                getPositionPercent={getPositionPercent}
                onEditAffectation={(aff) => {
                  setEditAffectation({
                    interventionId: aff.intervention_id,
                    numeroOrdre: aff.numero_ordre,
                    parking: aff.parking,
                    zone: aff.zone,
                    statut: aff.statut,
                    currentEquipeId: equipe.id,
                    heurePlanifiee: aff.heure_planifiee || aff.heure_souhaitee,
                    heureSouhaitee: aff.heure_souhaitee,
                  });
                }}
              />
            ))}

            {timeline.equipes.length === 0 && (
              <div className="text-center py-8 text-gray-500">Aucune equipe active</div>
            )}
          </div>
        )}
      </div>

      {editAffectation && (
        <EditAffectationModal
          affectation={editAffectation}
          equipes={timeline?.equipes || []}
          onClose={() => setEditAffectation(null)}
          onChangeEquipe={(equipeId) => {
            reassignMutation.mutate(
              {
                intervention_id: editAffectation.interventionId,
                zone: editAffectation.zone,
                equipe_id: equipeId,
              },
              {
                onSuccess: () => {
                  toast.success(`Equipe modifiee pour ${editAffectation.numeroOrdre}`);
                  setEditAffectation(null);
                },
                onError: () => toast.error("Erreur lors du changement d'equipe"),
              }
            );
          }}
          onChangeHeure={(heure) => {
            updateScheduleMutation.mutate(
              {
                intervention_id: editAffectation.interventionId,
                heure_planifiee: heure,
              },
              {
                onSuccess: () => {
                  toast.success('Horaire modifie');
                  setEditAffectation(null);
                },
                onError: () => toast.error("Erreur lors du changement d'heure"),
              }
            );
          }}
          isSavingEquipe={reassignMutation.isPending}
          isSavingHeure={updateScheduleMutation.isPending}
        />
      )}
    </div>
  );
}

function EquipeTimelineRow({
  equipe,
  totalWidth,
  getPositionPercent,
  onEditAffectation,
}: {
  equipe: TimelineEquipe;
  totalWidth: number;
  getPositionPercent: (iso: string | null) => number | null;
  onEditAffectation: (aff: TimelineEquipeAffectation) => void;
}) {
  return (
    <div className="flex border-b hover:bg-gray-50">
      <div className="w-40 flex-shrink-0 px-4 py-3 border-r bg-white sticky left-0 z-[1]">
        <div className="font-medium text-sm">{equipe.nom}</div>
        <div className="text-xs text-gray-500">{equipe.type_engin_label}</div>
        <div className="text-xs text-gray-400">{equipe.charge} affectation(s)</div>
      </div>
      <div className="relative h-16" style={{ width: `${totalWidth}px` }}>
        {equipe.affectations.map((aff, index) => {
          const left = getPositionPercent(aff.heure_planifiee_iso);
          if (left === null) return null;
          const colorClass = STATUT_TIMELINE_COLORS[aff.statut] || 'bg-gray-200 border-gray-400';

          return (
            <button
              key={index}
              onClick={() => onEditAffectation(aff)}
              className={`absolute top-3 h-10 px-2 rounded border-2 text-left hover:opacity-80 hover:shadow-md ${colorClass}`}
              style={{ left: `${left}%`, width: '90px' }}
            >
              <div className="text-xs font-semibold truncate">{aff.parking}</div>
              <div className="text-xs truncate">{aff.heure_planifiee}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default RegulationTimelinePage;
